/**
 * Bluff (Cheat / I Doubt It / "BS") — server-authoritative rules engine (four-player bluffing shedding game).
 *
 * Rules implemented:
 *  - One 52-card deck dealt out, 13 each. Play goes round the table and the required rank climbs with every play:
 *    A, 2, 3 … K, A, 2 …. The first player must claim aces.
 *  - On your turn you put one to four cards face down on the pile and say they are all of the required rank — they need
 *    not be. The cards stay hidden.
 *  - The next player may call "bluff" instead of playing. The cards just played are turned over: if they were not all
 *    of the claimed rank the player who played them takes the whole pile, otherwise the caller does. The pile is then
 *    empty and the caller plays next (the required rank carries on from the claim).
 *  - A player who plays their last cards has not won yet: the next player must either call or accept. If the cards
 *    were honest, or are accepted, that player wins; if they were a bluff they take the pile and play on.
 *  - The game ends at the first win. The others are ranked by how few cards they hold. A cap on turns ends a very long
 *    game the same way.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  standingsFromScores,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface BluffConfig {
  /** Hard cap on turns so a game always terminates. */
  maxTurns: number;
}

export type BluffPhase = 'PLAYING' | 'MATCH_END';

export interface BluffClaim {
  seat: number;
  /** Claimed rank, ace 1 … king 13. */
  rank: number;
  cards: CardId[];
}

export interface BluffReveal {
  claimer: number;
  challenger: number;
  rank: number;
  cards: CardId[];
  truthful: boolean;
  /** The seat that took the pile. */
  loser: number;
  pileSize: number;
}

export interface BluffState {
  config: BluffConfig;
  players: string[];
  phase: BluffPhase;
  hands: CardId[][];
  pile: CardId[];
  lastClaim: BluffClaim | null;
  /** The rank the next player must claim. */
  required: number;
  turn: number;
  turns: number;
  lastReveal: BluffReveal | null;
  winner: number | null;
}

export type BluffAction = { type: 'play'; cards: CardId[] } | { type: 'call' } | { type: 'accept' };

export interface BluffLegal {
  canPlay: boolean;
  canCall: boolean;
  /** The previous player has no cards left: call or accept their win. */
  canAccept: boolean;
  required: number;
}

export interface BluffView {
  gameType: 'BLUFF';
  config: BluffConfig;
  seat: number | null;
  phase: BluffPhase;
  hand: CardId[];
  handCounts: number[];
  pileCount: number;
  required: number;
  /** The last play: who, which rank they claimed and how many cards — never the cards themselves. */
  lastClaim: { seat: number; rank: number; count: number } | null;
  lastReveal: BluffReveal | null;
  turn: number;
  winner: number | null;
  totals: number[];
}

const DEFAULTS: BluffConfig = { maxTurns: 600 };
const SEATS = 4;
export const BLUFF_MAX_PLAY = 4;

export const bluffRank = (card: CardId): number => {
  const r = parseCard(card).rank;
  return r === 14 ? 1 : r;
};

export const bluffNextRank = (rank: number): number => (rank >= 13 ? 1 : rank + 1);

export function bluffLegal(state: Pick<BluffState, 'lastClaim' | 'hands' | 'required'>, seat: number): BluffLegal {
  const claim = state.lastClaim;
  const claimerOut = claim !== null && state.hands[claim.seat]!.length === 0;
  return {
    canPlay: !claimerOut && state.hands[seat]!.length > 0,
    canCall: claim !== null,
    canAccept: claimerOut,
    required: state.required,
  };
}

export const bluffEngine: GameEngine<BluffConfig, BluffState, BluffAction, BluffView, BluffLegal> = {
  gameType: 'BLUFF',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('maxTurns', c.maxTurns, 40, 5000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Bluff needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const deck = shuffle(buildDeck(), rng);
    const hands: CardId[][] = [[], [], [], []];
    deck.forEach((c, i) => hands[i % SEATS]!.push(c));
    return {
      config,
      players,
      phase: 'PLAYING',
      hands: hands.map((h) => sortHand(h)),
      pile: [],
      lastClaim: null,
      required: 1,
      turn: rng.int(SEATS),
      turns: 0,
      lastReveal: null,
      winner: null,
    };
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    if (raw.type === 'call') return { type: 'call' };
    if (raw.type === 'accept') return { type: 'accept' };
    if (raw.type === 'play' && Array.isArray(raw.cards) && raw.cards.length >= 1 && raw.cards.length <= BLUFF_MAX_PLAY && raw.cards.every((c) => typeof c === 'string' && c.length <= 4)) {
      return { type: 'play', cards: raw.cards as string[] };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Bluff action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase === 'PLAYING', 'GAME_OVER', 'The game is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const legal = bluffLegal(state, seat);

    if (action.type === 'accept') {
      assertEngine(legal.canAccept && state.lastClaim, 'ILLEGAL_MOVE', 'There is nothing to accept');
      finish(state, state.lastClaim.seat, events);
      return events;
    }

    if (action.type === 'call') {
      assertEngine(legal.canCall && state.lastClaim, 'ILLEGAL_MOVE', 'There is no play to challenge');
      const claim = state.lastClaim;
      const truthful = claim.cards.every((c) => bluffRank(c) === claim.rank);
      const loser = truthful ? seat : claim.seat;
      const pileSize = state.pile.length;
      state.hands[loser] = sortHand([...state.hands[loser]!, ...state.pile]);
      state.pile = [];
      state.lastReveal = { claimer: claim.seat, challenger: seat, rank: claim.rank, cards: [...claim.cards], truthful, loser, pileSize };
      state.lastClaim = null;
      state.turns++;
      events.push({ type: 'call', seat, claimer: claim.seat, truthful, loser, pileSize, cards: [...claim.cards] });
      // An honest last play wins on the spot.
      if (truthful && state.hands[claim.seat]!.length === 0) {
        finish(state, claim.seat, events);
        return events;
      }
      if (state.turns >= state.config.maxTurns) {
        finish(state, null, events);
        return events;
      }
      // The caller plays next, claiming the rank after the one just challenged.
      state.turn = seat;
      return events;
    }

    assertEngine(legal.canPlay, 'ILLEGAL_MOVE', 'You must call or accept first');
    const hand = state.hands[seat]!;
    assertEngine(new Set(action.cards).size === action.cards.length && action.cards.every((c) => hand.includes(c)), 'INVALID_CARD', 'Choose different cards from your hand');
    removeCards(hand, action.cards);
    state.pile.push(...action.cards);
    state.lastClaim = { seat, rank: state.required, cards: [...action.cards] };
    events.push({ type: 'play', seat, rank: state.required, count: action.cards.length });
    state.required = bluffNextRank(state.required);
    state.turns++;
    if (state.turns >= state.config.maxTurns) {
      finish(state, null, events);
      return events;
    }
    let next = (seat + 1) % SEATS;
    // A seat with no cards cannot play; the claim stands for whoever is next with cards.
    while (state.hands[next]!.length === 0 && next !== seat) next = (next + 1) % SEATS;
    state.turn = next;
    return events;
  },

  getActors(state) {
    return state.phase === 'PLAYING' ? [state.turn] : [];
  },

  getLegalActions(state, seat) {
    if (state.phase !== 'PLAYING' || seat !== state.turn) return { canPlay: false, canCall: false, canAccept: false, required: state.required };
    return bluffLegal(state, seat);
  },

  getTimeoutAction(state, seat) {
    const legal = bluffLegal(state, seat);
    if (!legal.canPlay) return legal.canAccept ? { type: 'accept' } : { type: 'call' };
    const hand = state.hands[seat]!;
    const honest = hand.filter((c) => bluffRank(c) === state.required);
    if (honest.length > 0) return { type: 'play', cards: honest.slice(0, BLUFF_MAX_PLAY) };
    return { type: 'play', cards: [hand[0]!] };
  },

  getView(state, seat) {
    return {
      gameType: 'BLUFF',
      config: state.config,
      seat,
      phase: state.phase,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      pileCount: state.pile.length,
      required: state.required,
      lastClaim: state.lastClaim ? { seat: state.lastClaim.seat, rank: state.lastClaim.rank, count: state.lastClaim.cards.length } : null,
      lastReveal: state.lastReveal ? { ...state.lastReveal, cards: [...state.lastReveal.cards] } : null,
      turn: state.turn,
      winner: state.winner,
      totals: state.hands.map((h) => h.length),
    };
  },

  getSystemStep: () => null,
  runSystemStep: () => [],

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return standings(state);
  },

  getRoundsCompleted: (state) => state.turns,
};

function finish(state: BluffState, winner: number | null, events: EngineEvent[]): void {
  state.phase = 'MATCH_END';
  state.winner = winner;
  events.push({ type: 'matchEnd', winner, standings: standings(state) });
}

/** The winner first, then everyone by how few cards they hold. */
function standings(state: BluffState): Standing[] {
  return standingsFromScores(
    state.hands.map((h, seat) => ({ seat, score: (state.winner === seat ? 1000 : 0) + (52 - h.length) })),
  );
}
